import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WorkOrder, Technician, WorkOrderStatus } from '@/types';
import { TechnicianService } from '@/services/technicianService';
import { SubscriptionService } from '@/services/subscriptionService';
import { validateTechnician, formatValidationErrors } from '@/utils/validation';
import { useToast } from '@/contexts/ToastContext';
import { queryKeys } from '@/hooks/queries/queryKeys';
import type { User } from '@supabase/supabase-js';

/**
 * Hooks con mutaciones de técnicos (P0.2).
 * Usado por DataProvider para reducir God Object.
 */
export function useTechnicianMutations(
    companyId: string,
    orders: WorkOrder[],
    technicians: Technician[],
    user: User | null
) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    const invalidateTechnicians = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.technicians });
    }, [queryClient]);
    const invalidateOrders = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.orders() });
    }, [queryClient]);

    const saveTechnician = useCallback(
        async (techData: Partial<Technician>, editingTechId?: string) => {
            const validationErrors = validateTechnician(techData);
            if (validationErrors.length > 0) {
                showToast(formatValidationErrors(validationErrors), 'error');
                throw new Error('Validation failed');
            }

            const withTimeout = (promise: Promise<any>, ms = 60000) =>
                Promise.race([
                    promise,
                    new Promise((_, reject) =>
                        setTimeout(() => reject(new Error('La operación tardó demasiado. Verifica tu conexión o intenta de nuevo en unos segundos.')), ms)
                    ),
                ]);

            if (editingTechId) {
                const updatedTech = {
                    ...technicians.find((t) => t.id === editingTechId)!,
                    ...techData,
                    id: editingTechId,
                } as Technician;
                await TechnicianService.updateTechnician(updatedTech);
                invalidateTechnicians();
            } else {
                if (!user) {
                    showToast('ERROR CRÍTICO: Usuario no encontrado', 'error');
                    throw new Error('No estás autenticado. Refresca la app.');
                }
                if (!companyId) {
                    throw new Error('No se pudo determinar el ID de la empresa. ¿Has completado el onboarding?');
                }
                const newTech: Technician = {
                    id: '',
                    name: techData.name || 'Nuevo Técnico',
                    avatar: techData.avatar,
                    category: techData.category || 'Operari@',
                    specialties: techData.specialties || [],
                    status: techData.status || 'active',
                    email: techData.email || '',
                    phone: techData.phone || '',
                    currentLocation: { lat: 0, lng: 0, address: 'Sin ubicación' },
                    color: techData.color || '#3b82f6',
                };
                try {
                    // V2150: Unificado con el sistema de invitaciones del onboarding
                    // Esto dispara el envío de email vía auth-emails y crea el registro en technicians y company_invitations
                    await withTimeout(SubscriptionService.createInvitation({
                        email: techData.email || '',
                        name: techData.name,
                        phone: techData.phone || undefined,
                        category: techData.category,
                        specialties: techData.specialties,
                        color: techData.color,
                        companyId: companyId
                    }));
                    invalidateTechnicians();
                } catch (err: any) {
                    showToast(err.message || 'Error al enviar invitación al técnico', 'error');
                    throw err;
                }
                const refreshedTechs = await withTimeout(TechnicianService.getTechnicians());
                queryClient.setQueryData(queryKeys.technicians, refreshedTechs);
            }
        },
        [companyId, technicians, user, invalidateTechnicians, queryClient, showToast]
    );

    const deleteTechnician = useCallback(
        async (techId: string) => {
            const activeOrders = orders.filter(
                (o) => o.technicianId === techId && o.status !== WorkOrderStatus.COMPLETED && !o.isArchived
            );
            if (activeOrders.length > 0) {
                throw new Error(
                    `No se puede eliminar el técnico. Tiene ${activeOrders.length} órdenes activas asignada(s).`
                );
            }
            try {
                await TechnicianService.deleteTechnician(techId);
                invalidateTechnicians();
                invalidateOrders();
            } catch (error) {
                console.error('Failed to delete technician from DB', error);
                showToast('Error al eliminar de la base de datos.', 'error');
                throw error;
            }
        },
        [orders, invalidateTechnicians, invalidateOrders, showToast]
    );

    return {
        saveTechnician,
        deleteTechnician,
    };
}
